import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';

import {
	type MessageInstanceEventHookScope,
	useMessageInstanceEvent as useMessageInstanceEventHook,
} from '@atlassian/message-instance-event-frontend-client/useMessageInstanceEvent';

import {
	type PostOfficeMessageReadActions,
	usePostOfficeMessageReadActions as usePostOfficeMessageReadActionsHook,
} from '@atlassian/post-office-analytics/read-state';

import {
	type PostOfficeAnalyticsHookScope,
	useFirePostOfficeAnalyticsEvents as useFirePostOfficeAnalyticsEventsHook,
} from '@post-office/analytics';

import {
	type MessageEventHookScope,
	useMessageEvent as useMessageEventHook,
} from '@post-office/message-lifecycle';

import type { State as InitialDataStates } from '@post-office/placement-contexts/hooks/types';

import { type SerializableRecord } from '@post-office/serializable-record';

import type { MessageCreationType } from '@post-office/message-template-contracts/message-template';

import { useEffect, useState } from 'react';

export enum State {
	Loading = 'LOADING',
	Data = 'DATA',
	Error = 'ERROR',
	NoNotifications = 'NO_NOTIFICATIONS',
}

const READ_ANALYTICS_HOOK_GATE = 'post-office_enable_read-analytics-hook_admin-hub';

type MessageStateReturnType = {
	currentState: State;
	currentMessages: SerializableRecord[] | undefined;
	handleMarkAllAsRead: (onlyShowUnreadToggle: boolean) => Promise<void>;
	handleReadStatusChange: (messageInstanceId: string, onlyShowUnreadToggle: boolean) => void;
};

export type MessagesStateOptions = {
	state: InitialDataStates<{ messages?: SerializableRecord[] }> & { product?: string };
	useMessageEvent?: () => Pick<MessageEventHookScope, 'markAllAsRead'>;
	useFirePostOfficeAnalyticsEvents?: () => PostOfficeAnalyticsHookScope;
	useMessageInstanceEvent?: () => MessageInstanceEventHookScope;
	usePostOfficeMessageReadActions?: () => Pick<PostOfficeMessageReadActions, 'markMessagesAsRead'>;
};

export const useMessagesState = ({
	state,
	useMessageEvent = useMessageEventHook,
	useFirePostOfficeAnalyticsEvents = useFirePostOfficeAnalyticsEventsHook,
	useMessageInstanceEvent = useMessageInstanceEventHook,
	usePostOfficeMessageReadActions = usePostOfficeMessageReadActionsHook,
}: MessagesStateOptions): MessageStateReturnType => {
	const [currentMessages, setCurrentMessages] = useState<SerializableRecord[] | undefined>(
		undefined,
	);
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();
	const { markAllAsRead } = useMessageEvent();
	const { messageInstanceClicked } = useMessageInstanceEvent();
	const { markMessagesAsRead } = usePostOfficeMessageReadActions();
	const shouldUseReadAnalyticsHook =
		FeatureGates.checkGate(READ_ANALYTICS_HOOK_GATE) ||
		FeatureGates.checkGate('post-office_enable_semantic-event-fixes');
	const handleMarkAllAsRead = async (onlyShowUnreadToggle: boolean) => {
		if (currentMessages) {
			const unreadMessages = currentMessages.filter((message) => message['readStatus'] !== 'read');
			const updatedMessagesToDisplay = onlyShowUnreadToggle
				? []
				: currentMessages.map((message) => ({ ...message, readStatus: 'read' }));
			setCurrentMessages(updatedMessagesToDisplay);
			try {
				await markAllAsRead(unreadMessages);
				if (shouldUseReadAnalyticsHook) {
					void markMessagesAsRead(
						unreadMessages.map((message) => ({
							messageInstanceId: message['messageInstanceId'] as string,
							messageTemplateId: message['messageTemplateId'] as string,
							messageCreationType: message['messageCreationType'] as MessageCreationType,
							triggerId: message['triggerId'] as string,
						})),
					);
				} else {
					for (const message of unreadMessages) {
						void messageInstanceClicked({
							messageInstanceId: message['messageInstanceId'] as string,
							messageTemplateId: message['messageTemplateId'] as string,
							messageCreationType: message['messageCreationType'] as MessageCreationType,
							messageTriggerId: message['triggerId'] as string,
							additionalMetadataFromFrontend: {},
							messageEventType: 'clicked',
							clickedElement: {
								clickIdentifier: 'mark-all-as-read',
								clickedElement: 'button',
								buttonType: 'primary',
							},
							action: {
								actionType: 'read',
							},
						});
					}
				}

				fireAnalyticsEvent({
					action: 'marked',
					actionSubject: 'notification',
					actionSubjectId: 'multiple',
					eventType: 'track',
					attributes: {
						notificationReadState: 'unread',
						toState: 'read',
						markType: 'multiple',
						count: unreadMessages.length,
						messageInstanceIds: unreadMessages.map((message) => message['messageInstanceId']),
					},
				});
			} catch (error) {
				fireAnalyticsEvent({
					action: 'error on markAllAsRead',
					actionSubject: 'dataPromise',
					eventType: 'operational',
					attributes: {
						errorMessage: error,
					},
				});
			}
		}
	};

	const handleReadStatusChange = (messageInstanceId: string, onlyShowUnreadToggle: boolean) => {
		if (!currentMessages) {
			return;
		}
		const updatedMessages = currentMessages.map((message) => {
			if (message['messageInstanceId'] === messageInstanceId) {
				return {
					...message,
					readStatus: message['readStatus'] === 'unread' ? 'read' : 'unread',
				};
			}
			return message;
		});
		if (onlyShowUnreadToggle) {
			setCurrentMessages(
				updatedMessages.filter((message) => message['messageInstanceId'] !== messageInstanceId),
			);
		} else {
			setCurrentMessages(updatedMessages);
		}
	};

	useEffect(() => {
		if (state.isLoading || state.error) {
			setCurrentMessages(undefined);
			return;
		}
		const messages = state?.data?.messages ?? [];
		if (messages.length === 0) {
			setCurrentMessages([]);
			return;
		}
		setCurrentMessages(messages);
		fireAnalyticsEvent({
			eventType: 'screen',
			name: 'navigationList',
		});
	}, [state.isLoading, state?.data?.messages, fireAnalyticsEvent]);
	let currentState;
	if (state.error) {
		currentState = State.Error;
	} else if (state.isLoading || !currentMessages) {
		currentState = State.Loading;
	} else if (currentMessages.length === 0) {
		currentState = State.NoNotifications;
	} else {
		currentState = State.Data;
	}
	return { currentState, currentMessages, handleMarkAllAsRead, handleReadStatusChange };
};
